import {
  Box,
  Button,
  Chip,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import PercentageIcon from "assets/icons/percentage.svg";
import { Field, Form, Formik } from "formik";
import { Category, fetchCategory } from "lib/features/category/categoryService";
import { useGetIndustryTypesQuery } from "lib/features/industries/industriesService";
import { Industry } from "lib/features/industries/types";
import { useUpadateBasicItemMutation } from "lib/features/item/itemService";
import { Item, UpdateBasicItemDto } from "lib/features/item/types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Label from "../NewItemForm/components/Label";
import PostScript from "../NewItemForm/components/PostScript";
import { editBasicFormValidation } from "./EditItem.schema";
import SaveItemModal from "./SaveItemModal";

interface EditItemFormProps {
  itemDetails: Item;
  itemId: number;
}

const EditBasicItemForm = ({ itemDetails, itemId }: EditItemFormProps) => {
  const navigate = useNavigate();
  const { data: industries } = useGetIndustryTypesQuery(null, {});
  const [selectedIndustry, setSelectedIndustry] = useState<number | null>(null);

  const [visibleSaveModal, setVisibleSaveModal] = useState<boolean>(false);

  const [upadateBasicItem, { isLoading: editLoading }] =
    useUpadateBasicItemMutation();

  const [categories, setCategories] = useState<Category[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);

  const [formValues, setFormValues] = useState<UpdateBasicItemDto>({
    item_id: itemId ?? 0,
    industry_ids: [],
    category_ids: [],
    commission: null,
    selling_points: "",
  });

  const theme = useTheme();

  const onSubmit = () => {
    setVisibleSaveModal(true);
  };

  const handleEditSubmit = async () => {
    await upadateBasicItem({
      id: Number(formValues?.item_id),
      data: formValues,
    });
    setVisibleSaveModal(false);
    navigate("/my-items");
  };

  useEffect(() => {
    const getCategories = async () => {
      try {
        const categories = await fetchCategory();
        setCategories(categories);
      } catch (error) {}
    };

    getCategories();
  }, []);

  useEffect(() => {
    if (selectedIndustry && categories) {
      let newCategories = categories?.filter(
        (category: Category) =>
          category?.category !== "All" &&
          (category?.category_type === selectedIndustry ||
            category?.category_type === 0)
      );
      setFilteredCategories(newCategories);
    }
  }, [categories, selectedIndustry]);

  useEffect(() => {
    if (itemDetails) {
      setSelectedIndustry(itemDetails?.industries[0]?.id);

      setFormValues({
        item_id: itemId,
        industry_ids:
          itemDetails?.industries?.map((industry) => industry.id) || [],
        category_ids:
          itemDetails?.categories?.map((category) => category.id) || [],
        commission: itemDetails?.commission ?? null,
        selling_points: itemDetails?.selling_points ?? "",
      });
    }
  }, [itemDetails]);

  return (
    <Box sx={{ marginTop: "15px" }}>
      <Typography variant="h1" component="h1">
        Edit Details
      </Typography>
      <Typography
        sx={{
          color: "gray",
          fontSize: "0.8rem",
        }}
      >
        *Required fields
      </Typography>

      {/* Form */}

      <Formik
        initialValues={formValues}
        validationSchema={editBasicFormValidation}
        enableReinitialize={true}
        onSubmit={onSubmit}
      >
        {({ values, errors, touched, setFieldValue }) => {
          const handleNumericChange = (
            e: React.ChangeEvent<any>,
            fieldName: string
          ) => {
            const { value } = e.target;
            if (/^\d*$/.test(value) || value === "") {
              const numericValue = value === "" ? null : Number(value);

              setFieldValue(fieldName, numericValue);
              setFormValues((prevValues: any) => ({
                ...prevValues,
                [fieldName]: numericValue,
              }));
            }
          };

          const handleSelectIndustryCategory = (industry_id: number) => {
            setSelectedIndustry(industry_id);
            if (!values.industry_ids.includes(industry_id)) {
              setFieldValue("industry_ids", [
                ...values.industry_ids,
                industry_id,
              ]);

              setFormValues((prevValues: any) => ({
                ...prevValues,
                industry_ids: [...prevValues.industry_ids, industry_id],
              }));
            }
          };

          const handleSelectCategory = (category_id: number) => {
            const isCategorySelected =
              values.category_ids.includes(category_id);

            if (isCategorySelected) {
              const newCategoryIds = values.category_ids.filter(
                (id) => id !== category_id
              );
              setFieldValue("category_ids", newCategoryIds);

              setFormValues((prevValues: any) => ({
                ...prevValues,
                category_ids: newCategoryIds,
              }));
            } else {
              setFieldValue("category_ids", [
                ...values.category_ids,
                category_id,
              ]);

              setFormValues((prevValues: any) => ({
                ...prevValues,
                category_ids: [...prevValues.category_ids, category_id],
              }));
            }
          };

          return (
            <Form>
              <Box sx={{ fontFamily: theme.typography.fontFamily }}>
                {/* Industries */}
                <Box sx={{ marginTop: "1rem" }}>
                  <Label label="Select Industries" required={true} />
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {industries &&
                      industries?.map((industryType: Industry) => {
                        const _selected = selectedIndustry === industryType?.id;

                        return (
                          <Chip
                            key={industryType.id}
                            label={industryType.industry}
                            onClick={() =>
                              handleSelectIndustryCategory(industryType.id)
                            }
                            variant={_selected ? "filled" : "outlined"}
                            color={_selected ? "primary" : "default"}
                            sx={{
                              margin: "0 0.5rem 0.5rem 0",
                              border: _selected ? "none" : "1px solid #000",
                            }}
                          />
                        );
                      })}
                  </Box>
                  {errors.industry_ids && touched.industry_ids && (
                    <Typography color="red" fontSize="12px">
                      {errors.industry_ids}
                    </Typography>
                  )}
                </Box>

                {/* Categories */}
                <Box sx={{ marginTop: "1rem" }}>
                  <Label label="Select Categories" required={true} />
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {filteredCategories &&
                      filteredCategories?.map((category: Category) => {
                        const _selected = values.category_ids.includes(
                          category?.id
                        );

                        return (
                          <Chip
                            key={category.id}
                            label={category.category}
                            onClick={() => handleSelectCategory(category.id)}
                            variant={_selected ? "filled" : "outlined"}
                            color={_selected ? "primary" : "default"}
                            sx={{
                              margin: "0 0.5rem 0.5rem 0",
                              border: _selected ? "none" : "1px solid #000",
                              cursor: "pointer",
                            }}
                          />
                        );
                      })}
                  </Box>
                  {errors.category_ids && touched.category_ids && (
                    <Typography color="red" fontSize="12px">
                      {errors.category_ids}
                    </Typography>
                  )}
                </Box>

                {/* Commission Rate */}
                <Box>
                  <Label label="Commission Rate" required={true} />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <Box sx={{ display: "flex", flex: 1 }}>
                      <Field
                        as={TextField}
                        name="commission"
                        fullWidth
                        onChange={(e: any) =>
                          handleNumericChange(e, "commission")
                        }
                        error={touched.commission && Boolean(errors.commission)}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        width: "50px",
                        border: "1px solid black",
                        justifyContent: "center",
                        paddingY: "15px",
                        borderRadius: "5px",
                      }}
                    >
                      <img src={PercentageIcon} alt="age-icon" />
                    </Box>
                  </Box>

                  {errors.commission && touched.commission && (
                    <Typography color="red" fontSize="12px">
                      {errors.commission}
                    </Typography>
                  )}
                </Box>

                {/* Selling Points */}
                <Box>
                  <Label
                    label="Selling Points and Important Info"
                    required={false}
                  />
                  <Typography marginBottom={"10px"}>
                    (Optional & Only Visible to Referees)
                  </Typography>
                  <Field
                    as={TextField}
                    name="selling_points"
                    fullWidth
                    multiline
                    rows={3}
                    onChange={(e: any) => {
                      const text = e.target.value;
                      const lines = text.split("\n");

                      // Process each line
                      const processedLines = lines.map(
                        (line: any, index: number) => {
                          if (
                            line.trim() === "" &&
                            index !== lines.length - 1
                          ) {
                            return "";
                          }

                          const cleanLine = line.replace(/^•\s?/, "");

                          return cleanLine.trim() === ""
                            ? ""
                            : `• ${cleanLine}`;
                        }
                      );
                      const bulletText = processedLines.join("\n");
                      setFieldValue("selling_points", bulletText);
                      setFormValues((prevValues: any) => ({
                        ...prevValues,
                        selling_points: bulletText,
                      }));
                    }}
                  />
                  <PostScript text="Let Referees know a few key points that will help sell this item! You can also add things like price is negotiable or times are flexible." />
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  sx={{ width: "100%", marginTop: "20px", paddingY: "10px" }}
                >
                  Save
                </Button>

                {/* SaveItemModal */}
                <SaveItemModal
                  open={visibleSaveModal}
                  close={() => setVisibleSaveModal(false)}
                  loading={editLoading}
                  onSubmit={handleEditSubmit}
                />
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};
export default EditBasicItemForm;
